import { ReactNode, useState, useMemo } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Stack,
    Typography,
    type ChipProps,
} from '@mui/material';
import {
    ChipField,
    RecordContextProvider,
    useListContext,
} from 'react-admin';
import { Chip } from '@mui/material';
import type { QuickRange } from './receiptHelpers';
import { quickDateRange } from './receiptHelpers';

/**
 * FilterSection: a bordered box with a title and legend, containing filter controls.
 */
export const FilterSection = ({
    title,
    legend,
    children,
}: {
    title: string;
    legend: string;
    children: ReactNode;
}) => (
    <Box
        component="section"
        aria-label={title}
        sx={{
            p: 1.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
        }}
    >
        <Typography variant="subtitle2" component="h3" gutterBottom>
            {title}
        </Typography>
        <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mb: 1.25 }}
        >
            {legend}
        </Typography>
        {children}
    </Box>
);

/**
 * Standard quick-range options for date filters.
 */
export const QUICK_RANGES: { range: QuickRange; label: string }[] = [
    { range: 'current_week', label: 'Current week' },
    { range: 'previous_week', label: 'Previous week' },
    { range: 'current_month', label: 'Current month' },
    { range: 'previous_month', label: 'Previous month' },
];

/**
 * A single active filter chip: id, label, and color.
 */
export type ActiveFilterChip = {
    id: string;
    label: string;
    color: NonNullable<ChipProps['color']>;
};

/**
 * ActiveFilterChips: renders a list of active filter chips or "None applied".
 */
export const ActiveFilterChips = ({ chips }: { chips: ActiveFilterChip[] }) => (
    <>
        {chips.length > 0 ? (
            chips.map(chip => (
                <RecordContextProvider key={chip.id} value={chip}>
                    <ChipField
                        source="label"
                        size="small"
                        color={chip.color}
                    />
                </RecordContextProvider>
            ))
        ) : (
            <Typography variant="caption" color="text.secondary">
                None applied
            </Typography>
        )}
    </>
);

/**
 * QuickRangeButtons: renders a row of quick-range selection buttons.
 * @param ranges - Array of { key, label, value: { transaction_date_gte, transaction_date_lte } }
 * @param filterValues - Current filter values to determine which button is active
 * @param onSelect - Callback when a quick range is clicked
 */
export interface QuickRangeOption {
    key: string;
    label: string;
    value: { transaction_date_gte: string; transaction_date_lte: string };
}

export const QuickRangeButtons = ({
    ranges,
    filterValues,
    onSelect,
}: {
    ranges: QuickRangeOption[];
    filterValues: Record<string, unknown>;
    onSelect: (value: { transaction_date_gte: string; transaction_date_lte: string }) => void;
}) => (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {ranges.map(({ key, label, value }) => {
            const active =
                filterValues?.transaction_date_gte === value.transaction_date_gte &&
                filterValues?.transaction_date_lte === value.transaction_date_lte;
            return (
                <Button
                    key={key}
                    size="small"
                    variant={active ? 'contained' : 'outlined'}
                    onClick={() => onSelect(value)}
                >
                    {label}
                </Button>
            );
        })}
    </Stack>
);

/**
 * quickRangeLabel: determines the label for the current date range.
 * If it matches a quick range, returns its label; otherwise returns "gte → lte" or null.
 */
export const quickRangeLabel = (
    filterValues: Record<string, unknown>,
    ranges: QuickRangeOption[]
): string | null => {
    const gte = filterValues?.transaction_date_gte;
    const lte = filterValues?.transaction_date_lte;

    if (!gte && !lte) return null;

    const quick = ranges.find(
        ({ value }) =>
            value.transaction_date_gte === gte &&
            value.transaction_date_lte === lte
    );

    if (quick) return quick.label;

    return `${gte ? String(gte) : '…'} → ${lte ? String(lte) : '…'}`;
};

/**
 * ClearFiltersButton: clears all filters except those in stickyKeys.
 * Stops propagation to prevent the accordion from toggling.
 */
export const ClearFiltersButton = ({ stickyKeys }: { stickyKeys: string[] }) => {
    const { filterValues, setFilters } = useListContext<any>();
    const hasFilters = Object.keys(filterValues || {}).some(
        key => !stickyKeys.includes(key)
    );

    if (!hasFilters) return null;

    return (
        <Button
            size="small"
            onClick={(e) => {
                e.stopPropagation();
                const stickyFilters: Record<string, unknown> = {};
                Object.keys(filterValues || {}).forEach(key => {
                    if (stickyKeys.includes(key)) {
                        stickyFilters[key] = filterValues![key as keyof typeof filterValues];
                    }
                });
                setFilters(stickyFilters, {});
            }}
        >
            Clear filters
        </Button>
    );
};

/**
 * ReceiptFiltersAccordion: a collapsible accordion showing active filter chips
 * in the summary and filter controls in the expanded content.
 */
export const ReceiptFiltersAccordion = ({
    idPrefix,
    chips,
    clearButton,
    children,
}: {
    idPrefix: string;
    chips: ActiveFilterChip[];
    clearButton: ReactNode;
    children: ReactNode;
}) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <Accordion
            expanded={expanded}
            onChange={(_, next) => setExpanded(next)}
            disableGutters
            elevation={0}
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
                overflow: 'hidden',
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${idPrefix}-filters-content`}
                id={`${idPrefix}-filters-header`}
                sx={{
                    px: 2,
                    minHeight: 56,
                    '& .MuiAccordionSummary-content': {
                        my: 1,
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        width: '100%',
                    },
                }}
            >
                <Typography variant="subtitle1" component="h2">
                    Filters
                </Typography>
                {!expanded && <ActiveFilterChips chips={chips} />}
                <Box
                    onClick={event => event.stopPropagation()}
                    onFocus={event => event.stopPropagation()}
                    sx={{ display: 'inline-flex', ml: 'auto' }}
                >
                    {clearButton}
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                <Stack spacing={2}>
                    {children}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};
